import { MachineType, MachineConfig, MACHINE_CONFIGS } from './TycoonObjects';

export interface TycoonStats {
  money: number;
  totalEarned: number;
  machineCount: number;
  incomePerSecond: number;
}

export class TycoonHUD {
  private container: HTMLDivElement;
  private moneyDisplay: HTMLDivElement;
  private incomeDisplay: HTMLDivElement;
  private statsPanel: HTMLDivElement;
  private shopPanel: HTMLDivElement;
  private shopContent: HTMLDivElement;
  private notificationArea: HTMLDivElement;
  private exitButton: HTMLButtonElement;
  private shopToggle: HTMLButtonElement;
  private upgradePanel: HTMLDivElement;
  private crosshair: HTMLDivElement;
  private clickMessage: HTMLDivElement;

  private isShopOpen: boolean = true;
  private isPointerLocked: boolean = false;
  private stats: TycoonStats = {
    money: 100,
    totalEarned: 0,
    machineCount: 0,
    incomePerSecond: 0,
  };

  private onExitCallback: (() => void) | null = null;
  private onBuyMachineCallback: ((type: MachineType) => boolean) | null = null;

  constructor() {
    this.container = this.createContainer();
    this.moneyDisplay = this.createMoneyDisplay();
    this.incomeDisplay = this.createIncomeDisplay();
    this.statsPanel = this.createStatsPanel();
    this.shopPanel = this.createShopPanel();
    this.shopContent = this.createShopContent();
    this.notificationArea = this.createNotificationArea();
    this.exitButton = this.createExitButton();
    this.shopToggle = this.createShopToggle();
    this.upgradePanel = this.createUpgradePanel();
    this.crosshair = this.createCrosshair();
    this.clickMessage = this.createClickMessage();

    this.shopPanel.appendChild(this.shopContent);
    this.container.appendChild(this.moneyDisplay);
    this.container.appendChild(this.incomeDisplay);
    this.container.appendChild(this.statsPanel);
    this.container.appendChild(this.shopPanel);
    this.container.appendChild(this.notificationArea);
    this.container.appendChild(this.exitButton);
    this.container.appendChild(this.shopToggle);
    this.container.appendChild(this.upgradePanel);
    this.container.appendChild(this.crosshair);
    this.container.appendChild(this.clickMessage);

    document.body.appendChild(this.container);
    this.addStyles();
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes moneyPop {
        0% { transform: scale(1); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
      }
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideOut {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
      @keyframes notificationSlide {
        0% { transform: translateX(100%); opacity: 0; }
        10% { transform: translateX(0); opacity: 1; }
        90% { transform: translateX(0); opacity: 1; }
        100% { transform: translateX(100%); opacity: 0; }
      }
      @keyframes pulse {
        0%, 100% { box-shadow: 0 0 10px rgba(255, 200, 0, 0.5); }
        50% { box-shadow: 0 0 25px rgba(255, 200, 0, 0.8); }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-5px); }
      }
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .tycoon-btn:hover {
        transform: translateY(-2px);
        filter: brightness(1.1);
      }
      .tycoon-btn:active {
        transform: translateY(0);
      }
      .shop-item:hover {
        transform: scale(1.02);
        border-color: rgba(255, 200, 0, 0.8);
      }
      .shop-item.locked {
        opacity: 0.5;
        filter: grayscale(0.5);
      }
      .shop-item.affordable {
        animation: pulse 2s infinite;
      }
    `;
    document.head.appendChild(style);
  }

  private createContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'tycoon-hud';
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 200;
      font-family: 'Segoe UI', 'Roboto', Arial, sans-serif;
      display: none;
    `;
    return container;
  }

  private createMoneyDisplay(): HTMLDivElement {
    const display = document.createElement('div');
    display.id = 'tycoon-money';
    display.style.cssText = `
      position: absolute;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      padding: 16px 40px;
      background: linear-gradient(135deg, rgba(255, 215, 0, 0.95) 0%, rgba(255, 180, 0, 0.95) 100%);
      border-radius: 50px;
      color: #5a4000;
      font-size: 32px;
      font-weight: bold;
      display: flex;
      align-items: center;
      gap: 12px;
      box-shadow:
        0 4px 15px rgba(255, 200, 0, 0.4),
        0 2px 4px rgba(0, 0, 0, 0.1),
        inset 0 2px 0 rgba(255, 255, 255, 0.3);
      border: 3px solid rgba(255, 255, 255, 0.5);
      text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
      pointer-events: auto;
    `;
    display.innerHTML = `
      <span style="font-size: 36px;">💰</span>
      <span id="money-value">$100</span>
    `;
    return display;
  }

  private createIncomeDisplay(): HTMLDivElement {
    const display = document.createElement('div');
    display.id = 'tycoon-income';
    display.style.cssText = `
      position: absolute;
      top: 85px;
      left: 50%;
      transform: translateX(-50%);
      padding: 8px 20px;
      background: linear-gradient(135deg, rgba(76, 175, 80, 0.9) 0%, rgba(56, 142, 60, 0.9) 100%);
      border-radius: 20px;
      color: white;
      font-size: 16px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 2px 10px rgba(76, 175, 80, 0.3);
      pointer-events: auto;
    `;
    display.innerHTML = `
      <span>📈</span>
      <span id="income-value">+$0/s</span>
    `;
    return display;
  }

  private createStatsPanel(): HTMLDivElement {
    const panel = document.createElement('div');
    panel.id = 'tycoon-stats';
    panel.style.cssText = `
      position: absolute;
      top: 20px;
      left: 20px;
      padding: 16px 20px;
      background: linear-gradient(180deg, rgba(40, 40, 50, 0.95) 0%, rgba(30, 30, 40, 0.95) 100%);
      border-radius: 16px;
      color: white;
      font-size: 14px;
      min-width: 180px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.1);
      pointer-events: auto;
      backdrop-filter: blur(10px);
    `;
    panel.innerHTML = `
      <div style="font-size: 18px; font-weight: bold; margin-bottom: 12px; color: #ffd700; display: flex; align-items: center; gap: 8px;">
        <span>🏭</span> Estatísticas
      </div>
      <div style="display: flex; flex-direction: column; gap: 8px;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="color: #aaa;">🍬 Máquinas:</span>
          <span id="stat-machines" style="font-weight: bold; color: #4fc3f7;">0</span>
        </div>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="color: #aaa;">💵 Total ganho:</span>
          <span id="stat-total" style="font-weight: bold; color: #81c784;">$0</span>
        </div>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="color: #aaa;">⚡ Renda/s:</span>
          <span id="stat-income" style="font-weight: bold; color: #ffb74d;">$0</span>
        </div>
      </div>
    `;
    return panel;
  }

  private createShopPanel(): HTMLDivElement {
    const panel = document.createElement('div');
    panel.id = 'tycoon-shop';
    panel.style.cssText = `
      position: absolute;
      top: 130px;
      right: 20px;
      width: 320px;
      max-height: calc(100vh - 180px);
      background: linear-gradient(180deg, rgba(40, 40, 50, 0.98) 0%, rgba(25, 25, 35, 0.98) 100%);
      border-radius: 20px;
      color: white;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      border: 1px solid rgba(255, 255, 255, 0.1);
      pointer-events: auto;
      backdrop-filter: blur(15px);
      overflow: hidden;
      animation: slideIn 0.3s ease-out;
    `;

    const header = document.createElement('div');
    header.style.cssText = `
      padding: 20px;
      background: linear-gradient(135deg, rgba(156, 39, 176, 0.3) 0%, rgba(103, 58, 183, 0.3) 100%);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    `;
    header.innerHTML = `
      <div style="font-size: 22px; font-weight: bold; display: flex; align-items: center; gap: 10px;">
        <span style="font-size: 28px;">🛒</span>
        <span style="background: linear-gradient(90deg, #ff6b9d, #c44cff, #6b9dff); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">
          Loja de Máquinas
        </span>
      </div>
      <div style="font-size: 12px; color: #aaa; margin-top: 6px;">Clique para comprar e posicionar</div>
    `;
    panel.appendChild(header);

    return panel;
  }

  private createShopContent(): HTMLDivElement {
    const content = document.createElement('div');
    content.id = 'shop-content';
    content.style.cssText = `
      padding: 16px;
      max-height: calc(100vh - 280px);
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 12px;
    `;

    // Add scrollbar styling
    content.style.scrollbarWidth = 'thin';
    content.style.scrollbarColor = 'rgba(255,255,255,0.3) transparent';

    // Create shop items for each machine
    const tiers = [
      { tier: 1, title: '🌟 Básico', color: '#4fc3f7' },
      { tier: 2, title: '⭐ Avançado', color: '#ffb74d' },
      { tier: 3, title: '💎 Premium', color: '#e040fb' },
    ];

    tiers.forEach(({ tier, title, color }) => {
      const tierHeader = document.createElement('div');
      tierHeader.style.cssText = `
        font-size: 14px;
        font-weight: bold;
        color: ${color};
        padding: 8px 0;
        border-bottom: 1px solid ${color}33;
        margin-top: ${tier > 1 ? '8px' : '0'};
      `;
      tierHeader.textContent = title;
      content.appendChild(tierHeader);

      Object.values(MACHINE_CONFIGS)
        .filter((config) => config.tier === tier)
        .forEach((config) => {
          content.appendChild(this.createShopItem(config));
        });
    });

    return content;
  }

  private createShopItem(config: MachineConfig): HTMLDivElement {
    const item = document.createElement('div');
    item.className = 'shop-item';
    item.dataset.type = config.type;
    item.style.cssText = `
      background: linear-gradient(135deg, rgba(60, 60, 70, 0.8) 0%, rgba(45, 45, 55, 0.8) 100%);
      border-radius: 12px;
      padding: 14px;
      cursor: pointer;
      transition: all 0.2s ease;
      border: 2px solid transparent;
      display: flex;
      gap: 12px;
      align-items: center;
    `;

    const iconColor = `rgb(${Math.floor(config.color.r * 255)}, ${Math.floor(config.color.g * 255)}, ${Math.floor(config.color.b * 255)})`;

    item.innerHTML = `
      <div style="
        width: 50px;
        height: 50px;
        background: linear-gradient(135deg, ${iconColor}33 0%, ${iconColor}66 100%);
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        flex-shrink: 0;
        border: 2px solid ${iconColor}88;
      ">${config.icon}</div>
      <div style="flex: 1; min-width: 0;">
        <div style="font-weight: bold; font-size: 14px; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
          ${config.name}
        </div>
        <div style="font-size: 11px; color: #aaa; margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
          ${config.description}
        </div>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="color: #ffd700; font-weight: bold; font-size: 15px;">$${config.cost.toLocaleString()}</span>
          <span style="color: #81c784; font-size: 12px;">+$${config.income}/s</span>
        </div>
      </div>
      <div class="lock-icon" style="
        position: absolute;
        right: 10px;
        top: 10px;
        font-size: 16px;
        display: none;
      ">🔒</div>
    `;

    item.addEventListener('click', () => {
      if (item.classList.contains('locked')) {
        this.showNotification('🔒 Desbloqueie ganhando mais dinheiro!', 'warning');
        return;
      }
      if (this.stats.money < config.cost) {
        this.showNotification('💸 Dinheiro insuficiente!', 'error');
        return;
      }
      if (this.onBuyMachineCallback) {
        const success = this.onBuyMachineCallback(config.type);
        if (success) {
          this.showNotification(`✅ ${config.name} comprada!`, 'success');
        }
      }
    });

    return item;
  }

  private createNotificationArea(): HTMLDivElement {
    const area = document.createElement('div');
    area.id = 'tycoon-notifications';
    area.style.cssText = `
      position: absolute;
      bottom: 100px;
      right: 20px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      pointer-events: none;
    `;
    return area;
  }

  private createExitButton(): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'tycoon-btn';
    button.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      padding: 12px 24px;
      font-size: 14px;
      font-weight: bold;
      color: white;
      background: linear-gradient(135deg, rgba(100, 100, 110, 0.9) 0%, rgba(70, 70, 80, 0.9) 100%);
      border: none;
      border-radius: 12px;
      cursor: pointer;
      pointer-events: auto;
      transition: all 0.2s ease;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
      display: flex;
      align-items: center;
      gap: 8px;
    `;
    button.innerHTML = '<span>🚪</span> Sair';
    button.addEventListener('click', () => {
      if (this.onExitCallback) {
        this.onExitCallback();
      }
    });
    return button;
  }

  private createShopToggle(): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'tycoon-btn';
    button.style.cssText = `
      position: absolute;
      bottom: 30px;
      right: 20px;
      padding: 14px 28px;
      font-size: 16px;
      font-weight: bold;
      color: white;
      background: linear-gradient(135deg, rgba(156, 39, 176, 0.9) 0%, rgba(123, 31, 162, 0.9) 100%);
      border: none;
      border-radius: 16px;
      cursor: pointer;
      pointer-events: auto;
      transition: all 0.2s ease;
      box-shadow: 0 4px 20px rgba(156, 39, 176, 0.4);
      display: flex;
      align-items: center;
      gap: 10px;
    `;
    button.innerHTML = '<span style="font-size: 20px;">🛒</span> Loja';
    button.addEventListener('click', () => {
      this.toggleShop();
    });
    return button;
  }

  private createUpgradePanel(): HTMLDivElement {
    const panel = document.createElement('div');
    panel.id = 'upgrade-panel';
    panel.style.cssText = `
      position: absolute;
      bottom: 30px;
      left: 50%;
      transform: translateX(-50%);
      padding: 16px 24px;
      background: linear-gradient(135deg, rgba(40, 40, 50, 0.95) 0%, rgba(30, 30, 40, 0.95) 100%);
      border-radius: 16px;
      color: white;
      display: none;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.1);
      pointer-events: auto;
      backdrop-filter: blur(10px);
    `;
    return panel;
  }

  public toggleShop(): void {
    this.isShopOpen = !this.isShopOpen;
    if (this.isShopOpen) {
      this.shopPanel.style.display = 'block';
      this.shopPanel.style.animation = 'slideIn 0.3s ease-out';
    } else {
      this.shopPanel.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => {
        this.shopPanel.style.display = 'none';
      }, 280);
    }
  }

  public showNotification(
    message: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'info'
  ): void {
    const notification = document.createElement('div');

    const colors = {
      success: 'linear-gradient(135deg, #4caf50, #388e3c)',
      error: 'linear-gradient(135deg, #f44336, #d32f2f)',
      warning: 'linear-gradient(135deg, #ff9800, #f57c00)',
      info: 'linear-gradient(135deg, #2196f3, #1976d2)',
    };

    notification.style.cssText = `
      padding: 14px 24px;
      background: ${colors[type]};
      border-radius: 12px;
      color: white;
      font-size: 14px;
      font-weight: 600;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
      animation: notificationSlide 3s ease-in-out forwards;
      display: flex;
      align-items: center;
      gap: 10px;
    `;
    notification.textContent = message;

    this.notificationArea.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 3000);
  }

  public updateStats(stats: Partial<TycoonStats>): void {
    this.stats = { ...this.stats, ...stats };

    // Update money display with animation
    const moneyValue = document.getElementById('money-value');
    if (moneyValue) {
      const newMoney = `$${Math.floor(this.stats.money).toLocaleString()}`;
      if (moneyValue.textContent !== newMoney) {
        moneyValue.textContent = newMoney;
        this.moneyDisplay.style.animation = 'none';
        void this.moneyDisplay.offsetWidth;
        this.moneyDisplay.style.animation = 'moneyPop 0.2s ease';
      }
    }

    // Update income display
    const incomeValue = document.getElementById('income-value');
    if (incomeValue) {
      incomeValue.textContent = `+$${this.stats.incomePerSecond.toFixed(1)}/s`;
    }

    // Update stats panel
    const statMachines = document.getElementById('stat-machines');
    const statTotal = document.getElementById('stat-total');
    const statIncome = document.getElementById('stat-income');

    if (statMachines) statMachines.textContent = this.stats.machineCount.toString();
    if (statTotal) statTotal.textContent = `$${Math.floor(this.stats.totalEarned).toLocaleString()}`;
    if (statIncome) statIncome.textContent = `$${this.stats.incomePerSecond.toFixed(1)}`;

    // Update shop items
    this.updateShopItems();
  }

  private updateShopItems(): void {
    const items = this.shopContent.querySelectorAll('.shop-item') as NodeListOf<HTMLDivElement>;
    items.forEach((item) => {
      const type = item.dataset.type as MachineType;
      const config = MACHINE_CONFIGS[type];

      const isUnlocked = this.stats.totalEarned >= config.unlockAt;
      const canAfford = this.stats.money >= config.cost;

      item.classList.toggle('locked', !isUnlocked);
      item.classList.toggle('affordable', isUnlocked && canAfford);

      const lockIcon = item.querySelector('.lock-icon') as HTMLDivElement;
      if (lockIcon) {
        lockIcon.style.display = isUnlocked ? 'none' : 'block';
      }
    });
  }

  public onExit(callback: () => void): void {
    this.onExitCallback = callback;
  }

  public onBuyMachine(callback: (type: MachineType) => boolean): void {
    this.onBuyMachineCallback = callback;
  }

  private createCrosshair(): HTMLDivElement {
    const crosshair = document.createElement('div');
    crosshair.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 20px;
      height: 20px;
      display: none;
    `;
    crosshair.innerHTML = `
      <div style="position: absolute; top: 50%; left: 0; right: 0; height: 2px; background: white; transform: translateY(-50%); box-shadow: 0 0 3px rgba(0,0,0,0.5);"></div>
      <div style="position: absolute; left: 50%; top: 0; bottom: 0; width: 2px; background: white; transform: translateX(-50%); box-shadow: 0 0 3px rgba(0,0,0,0.5);"></div>
    `;
    return crosshair;
  }

  private createClickMessage(): HTMLDivElement {
    const message = document.createElement('div');
    message.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 20px 40px;
      background: linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(20, 20, 30, 0.85) 100%);
      border-radius: 16px;
      color: white;
      font-size: 18px;
      font-weight: 600;
      text-align: center;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      border: 2px solid rgba(255, 200, 0, 0.5);
      backdrop-filter: blur(10px);
      pointer-events: auto;
      cursor: pointer;
      display: block;
    `;
    message.innerHTML = `
      <div style="font-size: 32px; margin-bottom: 12px;">🖱️</div>
      <div>Controles</div>
      <div style="font-size: 12px; color: #aaa; margin-top: 8px;">WASD para mover • Botão direito + mouse para olhar</div>
    `;

    // No pointer lock needed - hide message by default
    message.style.display = 'none';

    return message;
  }

  public setPointerLocked(locked: boolean): void {
    // Now "locked" means right mouse button is held (camera rotation mode)
    if (this.isPointerLocked !== locked) {
      this.isPointerLocked = locked;

      if (locked) {
        // Show crosshair when rotating camera
        this.crosshair.style.display = 'block';
      } else {
        // Hide crosshair when not rotating
        this.crosshair.style.display = 'none';
      }
      // Shop and controls are always available - no need to hide/show based on camera mode
    }
  }

  public show(): void {
    this.container.style.display = 'block';
  }

  public hide(): void {
    this.container.style.display = 'none';
  }

  public dispose(): void {
    this.container.remove();
  }
}
